import FormShimmer from "components/common/shimmer/FormShimmer ";
import Switch from "components/common/Switch";
import { useQueryParam } from "hooks/useQueryParam";
import { useSmartBack } from "hooks/useSmartBack";
import React, { useEffect, useState } from "react";
import { Col, Form as BForm, Row } from "react-bootstrap";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  addRole,
  getRoleDetails,
  getRolesPermissions,
  updateRole,
} from "store/slices/rolesSlice";
import { hasAtLeastOneViewPermission } from "./roleHelper";
import { showAlert } from "store/slices/alertSlice";
import { handleSubmitForm } from "helpers/apiUtils";
import { usePermission } from "hooks/usePermission";
import AddEditLoader from "components/common/detailsPage/AddEditLoader";
import NoPermission from "components/common/NoPermission";
import PageHeader from "components/common/PageHeader";
import SingleSelect from "components/common/SingleSelect";
import { ADMIN_ACCOUNT_TYPE } from "utils/constants";
import CustomField from "components/form/CustomField";
import { getRoleValidationSchema } from "utils/validations";

const AddEditRole = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const id = useQueryParam("id");
  const goBack = useSmartBack();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { canAction } = usePermission();
  const { canAdd, canEdit } = canAction("roles");

  const isEdit = mode === "edit";

  const { loading, isLoading, permissions, itemDetails } = useSelector(
    (state) => state.Roles,
  );

  const [accountType, setAccountType] = useState("");
  const [hasFullPermission, setHasFullPermission] = useState(false);
  const [selected, setSelected] = useState({});
  const [initialValues, setInitialValues] = useState({ name: "", nameAr: "" });

  useEffect(() => {
    if (!isEdit || !itemDetails || !permissions?.length) return;

    // 1️⃣ Set basic fields
    setInitialValues({
      name: itemDetails.name || "",
      nameAr: itemDetails.nameAr || "",
    });
    setHasFullPermission(itemDetails.hasFullPermission);
    setAccountType(itemDetails.roleType);

    // 2️⃣ Prepare selected permissions map
    const selectedMap = {};

    // initialize all modules with empty array
    permissions?.forEach((module) => {
      selectedMap[module.module] = [];
    });

    // fill selected permissions from itemDetails
    itemDetails?.permissions?.forEach((mod) => {
      if (selectedMap[mod.module]) {
        selectedMap[mod.module] = mod.permissions.map((perm) =>
          typeof perm === "string" ? perm : perm.key,
        );
      }
    });

    setSelected(selectedMap);
  }, [isEdit, itemDetails, permissions]);

  useEffect(() => {
    dispatch(getRolesPermissions());
  }, []);

  useEffect(() => {
    if (isEdit && id && canEdit) {
      dispatch(getRoleDetails(id));
    }
  }, [isEdit, id, dispatch, canEdit]);

  // toggle individual permission
  const togglePermission = (moduleKey, permissionKey) => {
    setSelected((prev) => {
      const current = prev[moduleKey] || [];
      const isView = permissionKey.toLowerCase().includes("view");

      // 🚫 If VIEW is unchecked → clear all permissions
      if (isView && current.includes(permissionKey)) {
        return { ...prev, [moduleKey]: [] };
      }

      // ✅ Toggle normally
      let updated = current.includes(permissionKey)
        ? current.filter((x) => x !== permissionKey)
        : [...current, permissionKey];

      // 🔁 If NON-VIEW permission added → auto-add VIEW
      if (!isView) {
        const viewPermission = permissions
          ?.find((m) => m.module === moduleKey)
          ?.permission.find((p) => p.key.toLowerCase().includes("view"))?.key;

        if (viewPermission && !updated.includes(viewPermission)) {
          updated = [viewPermission, ...updated];
        }
      }

      return { ...prev, [moduleKey]: updated };
    });
  };
  useEffect(() => {
    if (isEdit || !permissions?.length) return;

    const initialSelected = {};
    permissions.forEach((module) => {
      // Pre-select all dashboard permissions
      if (module.key === "dashboard") {
        initialSelected[module.module] = module.permission.map((p) => p.key);
      } else {
        initialSelected[module.module] = [];
      }
    });
    setSelected(initialSelected);
  }, [isEdit, permissions]);

  // final submit
  const handleSubmit = (values) => {
    const payload = {
      name: values.name,
      nameAr: values.nameAr,
      hasFullPermission,
      permissions: [],
      roleType: accountType,
    };

    // 2️⃣ Permission validation (only when not full access)
    if (!hasFullPermission) {
      const hasViewPermission = hasAtLeastOneViewPermission(
        permissions,
        selected,
      );

      if (!hasViewPermission) {
        dispatch(
          showAlert({
            text: t(
              "Please select at least one View permission when Full Permission is disabled",
            ),
            type: "error",
          }),
        );
        return;
      }
    }

    if (!hasFullPermission) {
      permissions?.forEach((module) => {
        if (selected[module.module].length > 0) {
          payload.permissions.push({
            module: module.module,
            permissions: selected[module.module],
          });
        }
      });
    }

    if (isEdit) {
      const updatedPayload = {
        ...payload,
        id,
      };
      handleSubmitForm({
        dispatch,
        action: updateRole,
        payload: updatedPayload,
        // onSuccess: () => navigate(-1),
      });
    } else {
      handleSubmitForm({
        dispatch,
        action: addRole,
        payload,
        onSuccess: () => navigate(-1),
      });
    }
  };

  if (!canEdit && isEdit) return <NoPermission />;
  if (!canAdd && !isEdit) return <NoPermission />;

  if (isEdit && loading)
    return (
      <AddEditLoader title={t("Edit Role & Permissions")} rows={10} cols={3} />
    );

  return (
    <div className="page-content roles_page">
      <PageHeader
        isBackBtn={true}
        title={
          isEdit ? t("Edit Role & Permissions") : t("Add Role & Permissions")
        }
      />
      <div className="common_card">
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={getRoleValidationSchema(t)}
          onSubmit={handleSubmit}
        >
          <Form>
            {/* Role Name Fields */}
            <Row className="mb-2">
              <Col md={4}>
                <div className="form_field">
                  <CustomField
                    label={t("Role Name (English)")}
                    name="name"
                    placeholder={t("Enter Role Name (English)")}
                    enableTranslate
                    translateTo="ar"
                    targetField="nameAr"
                    required
                  />
                </div>
              </Col>

              <Col md={4}>
                <div className="form_field">
                  <CustomField
                    label={t("Role Name (Arabic)")}
                    name="nameAr"
                    className="arabic_text"
                    isArabic
                    placeholder={t("Enter Role Name (Arabic)")}
                    enableTranslate
                    translateTo="en"
                    targetField="name"
                    required
                  />
                </div>
              </Col>
              <Col lg={4}>
                <div className="form_field">
                  <label>
                    {t("Account Type")}
                    <span className="text-danger">*</span>
                  </label>
                  <SingleSelect
                    value={accountType}
                    options={ADMIN_ACCOUNT_TYPE.map((accountType) => ({
                      ...accountType,
                      name: t(accountType.name),
                    }))}
                    handleSingleSelectChange={(e) => {
                      setAccountType(e.value);
                    }}
                    placeholder={t("Select option")}
                  />
                </div>
              </Col>

              <Col md={4}>
                <div className="form_field">
                  <Switch
                    label={t("Full Permission")}
                    checked={hasFullPermission}
                    infoText={t(
                      "When enabled, this role will have unrestricted access to all modules, actions, and settings.",
                    )}
                    handleOnChange={() =>
                      setHasFullPermission(!hasFullPermission)
                    }
                  />
                </div>
              </Col>
            </Row>

            {/* Hide permission UI when full access */}
            {!hasFullPermission && (
              <Row>
                {permissions?.map((module) => (
                  <Col md={4} key={module.id} className="mb-3">
                    <div className="single_permission">
                      <h6>
                        {t(
                          language === "ar" ? module.labelAr : module.label,
                        )}
                      </h6>

                      <div className="permission_wrapper">
                        {module.permission.map((perm) => (
                          <BForm.Check
                            key={perm.id}
                            id={`perm-${module.id}-${perm.id}`}
                            type="checkbox"
                            label={t(
                              language === "ar" ? perm.labelAr : perm.label,
                            )}
                            checked={
                              selected[module.module]?.includes(perm.key) ||
                              module.key === "dashboard" ||
                              false
                            }
                            onChange={() => {
                              if (module.key === "dashboard") {
                                return;
                              } else {
                                togglePermission(module.module, perm.key);
                              }
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            )}
            <div className="g_end gap-3">
              <button
                className="main_btn outline"
                type="button"
                onClick={goBack}
              >
                {t("Cancel")}
              </button>
              <button className="main_btn" type="submit" disabled={isLoading}>
                {t("Save Role")}
              </button>
            </div>
          </Form>
        </Formik>
      </div>
    </div>
  );
};

export default AddEditRole;
